import { useState } from 'react'

import { Features } from 'generated/featureDefinitions'
import {
  ExpensesPlanPageDocument,
  ExpensesSubscriptionsFeatureMembershipDocument,
  ExpensesSubscriptionsPartnerManagedDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import ExpensesSubscriptionUsersDatatable from 'modules/expenses/components/subscription'
import type { AddUsersDisabledReason } from 'modules/expenses/lib/RouteContextParams'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import AddUsersModal from './AddUsersModal'

const ExpensesPlanPage = () => {
  const { company } = useCurrentSession()
  const subscriptionId = company?.subscription?.id
  const { data, error } = useQuery(ExpensesPlanPageDocument, {
    variables: {
      featureIds: [Features.expenses_per_user_pricing_create_expenses],
      subscriptionId,
    },
    skip: !subscriptionId,
  })

  const { data: partnerManagedData } = useQuery(ExpensesSubscriptionsPartnerManagedDocument, {
    variables: { companyId: company.id },
  })

  const { data: featureMembershipData } = useQuery(ExpensesSubscriptionsFeatureMembershipDocument)

  const [modalOpen, setModalOpen] = useState(false)
  const expensesPerUserPricingAvailable =
    data?.companies?.featureAvailabilitiesConnection.nodes.find(
      (f) => f.id == Features.expenses_per_user_pricing_create_expenses
    )?.available ?? false

  const partnerManaged = partnerManagedData?.features.featuresConnection?.nodes?.some(
    (node) => node?.enabled
  )
  const managedByPartnersUserLimit = data?.billing.subscription?.expensesQuantityCeiling ?? 0
  const currentUserCount =
    featureMembershipData?.features.featureMembership?.membersConnection.totalCount ?? 0

  const disabledReason: AddUsersDisabledReason | undefined = (() => {
    if (!partnerManaged) return undefined
    if (managedByPartnersUserLimit <= 0) return 'no_ceiling'
    if (currentUserCount >= managedByPartnersUserLimit) return 'limit_reached'
    return undefined
  })()

  return (
    <ResponsivePage.Body
      loading={!data && !error}
      authorized={expensesPerUserPricingAvailable}
      error={error}
      header={{
        illustration: 'user-male-female',
        title: i18n.t('billing.subscription.plans.expenses.title'),
        description: i18n.t('billing.subscription.plans.expenses.description'),
      }}
    >
      <ExpensesSubscriptionUsersDatatable
        onAddPressed={() => setModalOpen(true)}
        managedByPartnersUserLimit={managedByPartnersUserLimit}
      />
      {modalOpen && (
        <AddUsersModal disabledReason={disabledReason} onClose={() => setModalOpen(false)} />
      )}
    </ResponsivePage.Body>
  )
}

export default ExpensesPlanPage
